// Métricas de la pestaña Análisis — lógica PURA (sin React, sin fetch), testeada en
// metricas.test.ts. Spec §7 de 2026-09-03-analisis-fidelidad-test-masivo-design.md.
//
// La DECISIÓN (aceptar / regenerar / pedir fotos) la toma el backend con sus reglas
// (`config.reglas_decision`, `fidelidad_regenerar`): acá no hay umbrales, solo lectura
// y agregación. Antes el front calculaba un "flag" propio; ya no.

import type { Accion, Caso, Resultado, RunResumen, TipoCaso, Universo } from './tipos';

/** Categoría de un tipo de error del catálogo, por prefijo del id (`producto_logo_marca` → producto). */
export type CategoriaError = 'producto' | 'modelo' | 'escena' | 'texto' | 'tecnica' | 'instruccion' | 'carga' | 'otro';

const CATEGORIAS: ReadonlySet<string> = new Set(['producto', 'modelo', 'escena', 'texto', 'tecnica', 'instruccion', 'carga']);

export function categoriaDeTipo(tipo: string): CategoriaError {
  const idx = tipo.indexOf('_');
  if (idx <= 0) return 'otro';
  const prefijo = tipo.slice(0, idx);
  return CATEGORIAS.has(prefijo) ? (prefijo as CategoriaError) : 'otro';
}

/** La acción que decidió el backend, o null si el análisis falló. */
export function accionDe(r: Resultado): Accion | null {
  return r.decision?.accion ?? null;
}

/** "Hay que rehacer": regenerar o pedir fotos. Es lo que se mide contra editadas (esperado) y control (falso positivo). */
export function esRehacer(r: Resultado): boolean {
  const a = accionDe(r);
  return a === 'regenerar' || a === 'pedir_fotos';
}

export function formatearUsd(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  if (n === 0) return 'US$ 0.00';
  return `US$ ${n.toFixed(n >= 1 ? 2 : 4)}`;
}

export function formatearMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

/** Fidelidad acotada a la escala vigente: un 5 de la escala vieja (1–5) vale como el máximo. */
export function fidelidadEnEscala(f: number, fidelidadMax: number): number {
  return Math.min(Math.max(1, f), fidelidadMax);
}

export interface ResumenTipoCaso {
  /** Filas con resultado (incluye fallidas). */
  n: number;
  /** Filas analizadas de verdad (sin `error_analisis`). */
  ok: number;
  fallidas: number;
  aceptar: number;
  regenerar: number;
  pedirFotos: number;
  /** regenerar + pedirFotos. */
  rehacer: number;
  /** 0-100, sobre `ok` (una fallida no es "aceptar"). 0 si no hay analizadas. */
  pctRehacer: number;
  /** Promedio de fidelidad (acotada a la escala) de las analizadas; null si no hay ninguna. */
  fidelidadPromedio: number | null;
  /** Conteo por fidelidad, claves 1..fidelidadMax. */
  distribucion: Record<number, number>;
  /** Suma de `cost_usd` de todas las filas (una fallida por refusal también gasta). */
  costUsd: number;
  /** Promedio de `duration_ms` de las filas que lo tienen; null si ninguna. */
  duracionPromedioMs: number | null;
  veredictos: { correcto: number; parcial: number; incorrecto: number; sin: number };
}

function resumir(filas: Resultado[], fidelidadMax: number): ResumenTipoCaso {
  const ok = filas.filter((r) => r.error_analisis === null);
  const fidelidades = ok
    .map((r) => r.fidelidad_producto)
    .filter((f): f is NonNullable<typeof f> => f !== null)
    .map((f) => fidelidadEnEscala(f, fidelidadMax));
  const distribucion: Record<number, number> = {};
  for (let f = 1; f <= fidelidadMax; f += 1) distribucion[f] = 0;
  for (const f of fidelidades) distribucion[f] = (distribucion[f] ?? 0) + 1;
  const cuenta = (a: Accion) => ok.filter((r) => accionDe(r) === a).length;
  const regenerar = cuenta('regenerar');
  const pedirFotos = cuenta('pedir_fotos');
  const rehacer = regenerar + pedirFotos;
  const conDuracion = filas.filter((r) => r.duration_ms !== null);
  const veredictos = { correcto: 0, parcial: 0, incorrecto: 0, sin: 0 };
  for (const r of filas) {
    if (r.veredicto_humano) veredictos[r.veredicto_humano.estado] += 1;
    else veredictos.sin += 1;
  }
  return {
    n: filas.length,
    ok: ok.length,
    fallidas: filas.length - ok.length,
    aceptar: cuenta('aceptar'),
    regenerar,
    pedirFotos,
    rehacer,
    pctRehacer: ok.length > 0 ? (rehacer / ok.length) * 100 : 0,
    fidelidadPromedio: fidelidades.length > 0 ? fidelidades.reduce((acc, f) => acc + f, 0) / fidelidades.length : null,
    distribucion,
    costUsd: filas.reduce((acc, r) => acc + (r.cost_usd ?? 0), 0),
    duracionPromedioMs:
      conDuracion.length > 0
        ? conDuracion.reduce((acc, r) => acc + (r.duration_ms ?? 0), 0) / conDuracion.length
        : null,
    veredictos,
  };
}

/** Métricas separadas por tipo de caso: editadas (rehacer ≈ acierto) y control (rehacer ≈ falso positivo). */
export function resumenPorTipoCaso(resultados: Resultado[], fidelidadMax: number): Record<TipoCaso, ResumenTipoCaso> {
  return {
    editada: resumir(resultados.filter((r) => r.tipo_caso === 'editada'), fidelidadMax),
    control: resumir(resultados.filter((r) => r.tipo_caso === 'control'), fidelidadMax),
  };
}

/**
 * El resumen como pares [etiqueta, valor] listos para una tabla. Es la ÚNICA fuente del
 * orden y el formato: la usan la vista de Corridas (una columna por tipo de caso) y
 * Comparar (columnas A/B), así las dos muestran exactamente lo mismo.
 */
export function filasResumen(r: ResumenTipoCaso): Array<[string, string]> {
  const v = r.veredictos;
  const escala = Object.keys(r.distribucion).map(Number).sort((a, b) => a - b);
  const max = escala[escala.length - 1] ?? 0;
  return [
    ['Casos', String(r.n)],
    ['Analizadas', String(r.ok)],
    ['Fallidas', String(r.fallidas)],
    ['Aceptar', String(r.aceptar)],
    ['Regenerar', String(r.regenerar)],
    ['Pedir fotos', String(r.pedirFotos)],
    ['% rehacer', r.ok > 0 ? `${Math.round(r.pctRehacer)}%` : '—'],
    ['Fidelidad prom.', r.fidelidadPromedio === null ? '—' : r.fidelidadPromedio.toFixed(2)],
    [`Distribución 1-${max}`, escala.map((f) => r.distribucion[f] ?? 0).join(' · ')],
    ['Costo', formatearUsd(r.costUsd)],
    ['Latencia prom.', formatearMs(r.duracionPromedioMs)],
    ['Veredictos', `${v.correcto} ok · ${v.parcial} parcial · ${v.incorrecto} mal · ${v.sin} sin`],
  ];
}

const STATUS_TERMINAL: ReadonlySet<string> = new Set(['succeeded', 'failed', 'partial']);

/**
 * ¿Hay que seguir polleando? Mientras falten filas Y el status no sea terminal. Una
 * corrida cancelada o fallida deja imágenes sin fila (spec §8), así que `hechas < total`
 * solo no alcanza; y `finished_at` lo setea el handler al final pase lo que pase.
 */
export function corridaEnCurso(run: RunResumen): boolean {
  if (run.finished_at) return false;
  if (run.status && STATUS_TERMINAL.has(run.status)) return false;
  return run.hechas < run.total;
}

/** Editadas y control por separado, en el orden original (casos de una corrida reutilizada). */
export function separarPorTipo(casos: Caso[]): { casos: Caso[]; control: Caso[] } {
  return {
    casos: casos.filter((c) => c.tipo_caso === 'editada'),
    control: casos.filter((c) => c.tipo_caso === 'control'),
  };
}

/**
 * El `universo` que devolvería el dataset, calculado localmente sobre las EDITADAS de una
 * corrida reutilizada (el universo del backend describe imágenes editadas: total, empresas
 * y prioridades son de ese conjunto; el control no entra).
 */
export function universoLocal(casos: Caso[]): Universo {
  const editadas = casos.filter((c) => c.tipo_caso === 'editada');
  return {
    total: editadas.length,
    empresas: new Set(editadas.map((c) => c.empresa.id_company)).size,
    prio1: editadas.filter((c) => c.prioridad === 1).length,
    prio2: editadas.filter((c) => c.prioridad === 2).length,
    prio3: editadas.filter((c) => c.prioridad === 3).length,
  };
}
